/* tslint:disable:member-ordering */
// TODO: remove line below after fixing tests
/* istanbul ignore file */
import {
  SessionManagerInterface,
  StateType as SessionManagerStateType,
} from '@central-products/interface-session-manager';
import {
  ObSessionServiceInterface,
  ObSessionStateType,
  OnStateChangedCallback,
} from '@central-products/interface-obsession-service';
import { ObSessionServiceConfig, ObSessionServiceDependencies } from './types';
import { ObSessionServiceErrors } from './enums';

export class ObSessionService extends ObSessionServiceInterface {
  public static SESSION_SYNC_IDENTIFIER = 'OB_SESS_SYNC_KEY';
  private readonly shouldFollowLogout: boolean = false;
  private readonly obSessionServiceUrl: string;
  private readonly casUrl: string;
  private readonly sessionManager: SessionManagerInterface;
  private state: ObSessionStateType = ObSessionStateType.SESSION_UNKNOWN;
  private onStateChangedCallback?: OnStateChangedCallback;
  private abortController: AbortController = new AbortController();
  private loginAwaiters: (({}) => void)[] = [];

  constructor(
    config: ObSessionServiceConfig,
    // @ts-ignore
    dependencies: ObSessionServiceDependencies
  ) {
    super();

    if (!config.obSessionServiceUrl) {
      throw new Error(ObSessionServiceErrors.MISSING_SERVICE_URL);
    }

    if (!config.casUrl) {
      throw new Error(ObSessionServiceErrors.MISSING_CAS_URL);
    }

    if (typeof config.shouldFollowLogout !== 'undefined') {
      this.shouldFollowLogout = config.shouldFollowLogout;
    }

    const { obSessionServiceUrl, casUrl } = config;

    this.obSessionServiceUrl = obSessionServiceUrl.endsWith('/') ? obSessionServiceUrl : `${obSessionServiceUrl}/`;
    this.casUrl = casUrl.endsWith('/') ? casUrl : `${casUrl}/`;
    this.sessionManager = dependencies['session-manager'];
  }

  public async init() {
    const sessionManagerState = await this.sessionManager.getState();
    this.sessionManager.onStateChanged(this.processSessionManagerState);
    this.processSessionManagerState(sessionManagerState);
  }

  private processSessionManagerState = async ({ state }: { state: SessionManagerStateType }) => {
    if (state === SessionManagerStateType.LOGGED_IN) {
      const { sessionId } = await this.sessionManager.getSessionIdentifier();
      this.login(sessionId);
    } else if (state === SessionManagerStateType.NOT_LOGGED_IN) {
      this.logout();
    }
  };

  public async getState() {
    return { state: this.state };
  }

  public onStateChanged(callback: OnStateChangedCallback): void {
    this.onStateChangedCallback = callback;
  }

  public awaitLoggedIn(): Promise<{}> {
    if (this.state === ObSessionStateType.SESSION_EXISTS) {
      return Promise.resolve({});
    }

    return new Promise<{}>((resolve) => this.loginAwaiters.push(resolve));
  }

  private resolveLoginAwaiters() {
    let waiter;
    // tslint:disable-next-line no-conditional-assignment
    while ((waiter = this.loginAwaiters.pop())) {
      waiter({});
    }
  }

  private async login(sessionId: string | null) {
    if (this.state !== ObSessionStateType.NO_SESSION && this.state !== ObSessionStateType.SESSION_UNKNOWN) {
      return;
    }

    if (await this.hasValidSession(sessionId)) {
      this.setSessionSyncIdentifier(sessionId as string);
      this.setState(ObSessionStateType.SESSION_EXISTS);
      return;
    }

    const previousState = this.state;

    this.setState(ObSessionStateType.SESSION_REQUESTED);

    try {
      await this.initSession();

      this.setSessionSyncIdentifier(sessionId as string);
      this.setState(ObSessionStateType.SESSION_EXISTS);
    } catch (error) {
      this.setState(previousState);
      throw new Error(ObSessionServiceErrors.UNABLE_TO_INITIALIZE);
    }
  }

  private async logout() {
    if (this.state === ObSessionStateType.NO_SESSION) {
      return;
    }

    if (this.state === ObSessionStateType.SESSION_UNKNOWN) {
      this.clearSessionSyncIdentifier();
      this.setState(ObSessionStateType.NO_SESSION);
      return;
    }

    if (this.state === ObSessionStateType.SESSION_REQUESTED) {
      this.abortController.abort();
    }

    const previousState = this.state;

    try {
      await this.destroySession();

      this.clearSessionSyncIdentifier();
      this.setState(ObSessionStateType.NO_SESSION);
    } catch (error) {
      this.setState(previousState);
      throw new Error(ObSessionServiceErrors.UNABLE_TO_DESTROY_SESSION);
    }
  }

  private async initSession(): Promise<void> {
    try {
      await this.fetch(`${this.obSessionServiceUrl}?action=GoCasLogin&target=${this.casUrl}imAlive`, {
        credentials: 'include',
        signal: this.abortController.signal,
        mode: 'no-cors',
      });
    } catch (error) {
      if (!(await this.hasEstabilishedSession())) {
        throw error;
      }
    }
  }

  private async destroySession(): Promise<void> {
    try {
      await this.fetch(`${this.obSessionServiceUrl}?action=DoLogout`, {
        credentials: 'include',
        redirect: this.shouldFollowLogout ? 'follow' : 'manual',
        mode: 'cors',
      });
    } catch (error) {
      if (await this.hasEstabilishedSession()) {
        throw error;
      }
    }
  }

  private async fetch(url: string, params: RequestInit): Promise<void> {
    // no-cors mode is used to mitigate problems when non-Sports
    // verticals get error that 'null' origin has been blocked by CORS policy
    const { status } = await fetch(url, params);
    if (status >= 400) {
      throw new Error(`Failed to fetch ${url}. Status: ${status}`);
    }
  }

  private setState = (state: ObSessionStateType) => {
    if (this.state === state) {
      return;
    }

    this.state = state;

    if (state === ObSessionStateType.SESSION_EXISTS) {
      this.resolveLoginAwaiters();
    }

    if (this.onStateChangedCallback) {
      this.onStateChangedCallback({ state });
    }
  };

  private async hasValidSession(sessionId: string | null): Promise<boolean> {
    const shouldValidateSession =
      !this.hasSessionSyncIdentifier() || (sessionId != null && this.getSessionSyncIdentifier() === sessionId);

    if (shouldValidateSession) {
      return this.hasEstabilishedSession();
    }

    return false;
  }

  /**
   * Open bet session is managed by calling CAS & open bet in a chain of redirects.
   * Login: browser -> OB (start login & clear cookies) -> CAS (attach serviceTicket to OB login url)
   * -> OB (actual login, setting cookies) -> cas/imAlive (doesn't bring any value, validates only cas session, looks like workaround)
   * Logout: browser -> OB (remove cookies & session) -> we should not follow the rest redirects
   * during this chain CUST_SSL_COOKIE is being set/removed.
   * There are 2 known issues may happen during this chain.
   * - redirect to cas/imAlive or any other failed due to CORS issue
   * - redirects ends up in the infinite loop.
   * In both cases OB session will be still successfully created
   * We also don't want to call GoCasLogin action in case OB session already valid (browser refresh)
   * There is an OpenBet endpoint dedicated to check current cust_ssl_cookie
   * It will respond only with a HTTP status:
   * 200: Session is open and valid
   * 401: No session could be found or session has expired
   * 403: Session is not valid
   * 500: An error occurred when validating session
   */
  private async hasEstabilishedSession() {
    try {
      const response = await fetch(`${this.obSessionServiceUrl}?action=CheckCustSession`, {
        credentials: 'include',
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  private getSessionSyncIdentifier = () => {
    return window.localStorage?.getItem?.(ObSessionService.SESSION_SYNC_IDENTIFIER) ?? null;
  };

  private hasSessionSyncIdentifier = () => {
    return window.localStorage?.getItem?.(ObSessionService.SESSION_SYNC_IDENTIFIER) != null;
  };

  private setSessionSyncIdentifier = (sessionId: string) => {
    window.localStorage?.setItem?.(ObSessionService.SESSION_SYNC_IDENTIFIER, sessionId);
  };

  private clearSessionSyncIdentifier = () => {
    window.localStorage?.removeItem?.(ObSessionService.SESSION_SYNC_IDENTIFIER);
  };
}
